<template>
	<base-modal
		ref="$modal"
		:loading="isLoading"
		title="Бизнес-логика этапа"
		width="600px"
		height="600px"
	>
		<div v-if="!isLoading && logic" class="stage-logic">
			<section
				v-for="section in sections"
				:key="section.key"
				class="stage-logic__section"
			>
				<div
					class="stage-logic__section-title"
					@click="visibility[section.key] = !visibility[section.key]"
				>
					<span class="stage-logic__section-title-text">{{ section.title }}</span>
				</div>
				<template v-if="visibility[section.key]">
					<div
						v-if="section.items?.length"
						class="stage-logic__section-body"
					>
						<div
							v-for="item in section.items"
							:key="`${section.key}-${item.Id}`"
							class="stage-logic__section-item"
						>
							{{ item.Name }}
						</div>
					</div>
					<div v-else class="stage-logic__section-empty">—</div>
				</template>
			</section>
		</div>
	</base-modal>
</template>

<script setup lang="ts">
import {
	computed,
	reactive,
	ref
} from "vue";
import BaseModal from "@/components/common/modal/BaseModal.vue";
import { ProjectApi } from "@/ api/ProjectApi";
import type { StageBusinessLogic } from "@/models/interfaces/project/project-models";
import type { CommonEntity } from "@/models/interfaces/common";

const $modal = ref<typeof BaseModal | null>(null);
const isLoading = ref(false);
const logic = ref<StageBusinessLogic | null>(null);

const visibility = reactive({
	settersTo: false,
	settersFrom: false,
	target: false,
	source: false,
});

type SectionKey = keyof typeof visibility;

const sections = computed<{ key: SectionKey; title: string; items: CommonEntity[] }[]>(() => {
	if (!logic.value) return [];

	return [
		{ key: "settersTo", title: "Сотрудники, устанавливающие этап", items: logic.value.SettersToStage },
		{ key: "settersFrom", title: "Сотрудники, снимающие этап", items: logic.value.SettersFromStage },
		{ key: "target", title: "Следующие этапы", items: logic.value.TargetStages },
		{ key: "source", title: "Предыдущие этапы", items: logic.value.SourceStages },
	];
});

const onOpen = async (stageId: number) => {
	try {
		isLoading.value = true;
		logic.value = null;
		$modal.value?.onOpen();
		logic.value = await ProjectApi.getStageBusinessLogic(stageId);
	} catch (e) {
		console.error(e);
	} finally {
		isLoading.value = false;
	}
};

defineExpose({ onOpen });
</script>

<style scoped lang="scss">
@import "@/styles/color.scss";

.stage-logic {
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 16px;

	&__section {
		display: flex;
		flex-direction: column;
		gap: 6px;

		&-title {
			display: inline-flex;
			cursor: pointer;
			font-weight: 600;
			font-size: 14px;
			line-height: 18px;
			color: $color-text_main;

			&-text {
				border-bottom: 1px dashed #979797;

				&:hover {
					color: #396742;
				}
			}
		}

		&-body {
			display: flex;
			flex-direction: column;
			gap: 4px;
			margin-left: 12px;
		}

		&-item {
			font-size: 14px;
			line-height: 18px;
			color: $color-text_main;
		}

		&-empty {
			margin-left: 12px;
			font-size: 14px;
			line-height: 18px;
			color: #979797;
		}
	}
}
</style>
